import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Button, Chip,
  TextField, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, Stack, Tooltip, IconButton, MenuItem,
} from '@mui/material';
import { AssignmentInd, Refresh, Inventory2 } from '@mui/icons-material';
import api from '../../services/api';
import { useAuthStore } from '../../store/auth';

// Unassigned Numbers — the available inventory (no customer). Shows who each number was ordered for
// (carrier order) so allocation is deliberate and traceable. Assign one or many to a customer here;
// the number is then offline until it is put live on the DID Numbers page or the customer's DID tab.
export const UnassignedNumbersPage: React.FC = () => {
  const { user } = useAuthStore();
  const canWrite = ['super_admin', 'provider_admin', 'technical_admin', 'reseller_admin'].includes(user?.role || '');
  const [rows, setRows] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [carriers, setCarriers] = useState<any[]>([]);
  const [orderedForOpts, setOrderedForOpts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [orderedFor, setOrderedFor] = useState('');
  const [country, setCountry] = useState('');
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [msg, setMsg] = useState<{ sev: 'success' | 'error'; text: string } | null>(null);
  const [assignDialog, setAssignDialog] = useState<{ ids: string[]; numbers: string[] } | null>(null);
  const [assignTenant, setAssignTenant] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ unassigned: 'true', status: 'available', limit: '2000' });
      if (search) p.set('search', search);
      if (orderedFor) p.set('ordered_for', orderedFor);
      if (country) p.set('country', country);
      const { data } = await api.get(`/numbers?${p.toString()}`);
      setRows(Array.isArray(data) ? data : (data?.data || [])); setSel({});
    } catch (e: any) { setMsg({ sev: 'error', text: e.response?.data?.message || 'Failed to load numbers' }); }
    finally { setLoading(false); }
  }, [search, orderedFor, country]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/tenants?limit=500&tier=customer').then(r => setTenants(r.data?.data || r.data || [])).catch(() => {});
    api.get('/carriers?limit=200').then(r => setCarriers(r.data?.data || r.data || [])).catch(() => {});
    api.get('/numbers/ordered-for').then(r => setOrderedForOpts(r.data || [])).catch(() => {});
  }, []);

  const carrierName = (id: string) => carriers.find((c: any) => c.id === id)?.name || '—';
  const selIds = () => Object.keys(sel).filter(k => sel[k]);
  const allSelected = rows.length > 0 && rows.every(r => sel[r.id]);

  const doAssign = async () => {
    if (!assignDialog || !assignTenant) return;
    setBusy(true);
    try {
      if (assignDialog.ids.length === 1) await api.patch(`/numbers/${assignDialog.ids[0]}/assign`, { tenant_id: assignTenant });
      else await api.post('/numbers/bulk/assign', { ids: assignDialog.ids, tenant_id: assignTenant });
      const t = tenants.find((x: any) => x.id === assignTenant)?.name || 'customer';
      setMsg({ sev: 'success', text: `${assignDialog.ids.length} number(s) assigned to ${t}. They are offline until put live on the DID Numbers page.` });
      setAssignDialog(null); setAssignTenant(''); load();
    } catch (e: any) { setMsg({ sev: 'error', text: e.response?.data?.message || 'Assign failed' }); }
    finally { setBusy(false); }
  };

  const byOrdered: Record<string, number> = {};
  rows.forEach(r => { const k = r.ordered_for || '(no order info)'; byOrdered[k] = (byOrdered[k] || 0) + 1; });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" fontWeight={700}><Inventory2 sx={{ verticalAlign: 'middle', mr: 1 }} />Unassigned Numbers</Typography>
          <Typography variant="body2" color="text.secondary">
            Available inventory with no customer. "Ordered for" shows the end customer on the carrier order, so numbers are allocated to the right account.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <TextField size="small" placeholder="Search number" value={search} onChange={e => setSearch(e.target.value)} />
          <TextField select size="small" label="Country" value={country} onChange={e => setCountry(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="UK">UK</MenuItem>
            <MenuItem value="USA">USA</MenuItem>
          </TextField>
          <TextField select size="small" label="Ordered for" value={orderedFor} onChange={e => setOrderedFor(e.target.value)} sx={{ minWidth: 220 }}>
            <MenuItem value="">All</MenuItem>
            {orderedForOpts.map((o: any) => <MenuItem key={o.value} value={o.value}>{o.value} ({o.available} available)</MenuItem>)}
          </TextField>
          <Button startIcon={<Refresh />} variant="outlined" size="small" onClick={load}>Refresh</Button>
          {canWrite && <Button startIcon={<AssignmentInd />} variant="contained" size="small" disabled={!selIds().length}
            onClick={() => setAssignDialog({ ids: selIds(), numbers: rows.filter(r => sel[r.id]).map(r => r.number) })}>Assign selected ({selIds().length})</Button>}
        </Stack>
      </Box>

      {msg && <Alert severity={msg.sev} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Chip label={`${rows.length} unassigned`} color="primary" />
        {Object.entries(byOrdered).sort((a, b) => b[1] - a[1]).map(([k, v]) => <Chip key={k} label={`ordered for ${k}: ${v}`} variant="outlined" size="small" onClick={() => setOrderedFor(k.startsWith('(') ? '' : k)} />)}
      </Stack>

      <Card><CardContent>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell padding="checkbox">{canWrite && <Checkbox size="small" checked={allSelected} indeterminate={!allSelected && selIds().length > 0}
              onChange={e => { const n: Record<string, boolean> = {}; if (e.target.checked) rows.forEach(r => { n[r.id] = true; }); setSel(n); }} />}</TableCell>
            {['Number', 'Country', 'Area', 'Carrier', 'Ordered for', 'Order ref', 'In inventory since', ''].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
          </TableRow></TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id} hover>
                <TableCell padding="checkbox">{canWrite && <Checkbox size="small" checked={!!sel[r.id]} onChange={e => setSel({ ...sel, [r.id]: e.target.checked })} />}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.number}</TableCell>
                <TableCell>{r.country_code || '—'}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.area_name ? <>{r.area_name} <span style={{ color: '#999' }}>({r.area_code})</span></> : '—'}</TableCell>
                <TableCell>{carrierName(r.carrier_id)}</TableCell>
                <TableCell>{r.ordered_for || <em style={{ color: '#bbb' }}>—</em>}</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{r.order_ref || '—'}</TableCell>
                <TableCell>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</TableCell>
                <TableCell align="right">{canWrite && <Tooltip title="Assign to customer"><IconButton size="small" color="primary" onClick={() => setAssignDialog({ ids: [r.id], numbers: [r.number] })}><AssignmentInd fontSize="small" /></IconButton></Tooltip>}</TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No unassigned numbers</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={!!assignDialog} onClose={() => setAssignDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign {assignDialog?.ids.length} number(s) to a customer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: 'monospace', fontSize: 12, maxHeight: 120, overflow: 'auto' }}>
            {assignDialog?.numbers.join(', ')}
          </Typography>
          <TextField select fullWidth label="Customer *" value={assignTenant} onChange={e => setAssignTenant(e.target.value)}>
            {tenants.map((t: any) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </TextField>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            The delivery target defaults to the customer's SIP trunk. Numbers stay offline (503) until switched live on the DID Numbers page.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(null)}>Cancel</Button>
          <Button variant="contained" startIcon={<AssignmentInd />} disabled={busy || !assignTenant} onClick={doAssign}>Assign</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
